import type { PaymentIssue } from '@/services/billing-api'

/**
 * Üretim DIŞI E2E test hook'u.
 *
 * `sensei:e2e-auth=granted` anahtarı, Playwright koşumlarının gerçek Supabase oturumu
 * kurmadan uygulamayı gezebilmesini sağlar. Tek meşru kullanım budur.
 *
 * **Neden tek yerde tanımlı:** aynı koşul birden fazla yerde kopyalanırsa biri
 * güncellenip diğeri unutulabilir; o an bypass'ın kapsamı sessizce ayrışır. #492'de
 * tam olarak bu oldu — auth bypass'ı çalışırken entitlement tarafı bypass'tan habersizdi
 * ve `proOnly` sayfalar test edilemez hâle geldi.
 *
 * **Güvenlik sınırı:** `import.meta.env.MODE === 'production'` olan hiçbir build'de
 * `true` dönmez. Dağıtılan uygulamada localStorage'a bu anahtarı yazmak hiçbir şey
 * kazandırmaz. Bu sınır testle korunur (`__tests__/e2e-auth.test.ts`).
 */
export const E2E_AUTH_STORAGE_KEY = 'sensei:e2e-auth'
export const E2E_AUTH_GRANTED = 'granted'

export function isE2EAuthBypassEnabled(): boolean {
  if (import.meta.env.MODE === 'production') {
    return false
  }
  if (typeof window === 'undefined') {
    return false
  }
  try {
    return window.localStorage.getItem(E2E_AUTH_STORAGE_KEY) === E2E_AUTH_GRANTED
  } catch (error) {
    console.warn('[e2e-auth] bypass kontrolü sırasında hata:', error)
    return false
  }
}

/**
 * Entitlement (ödeme) bypass'ı için DAHA DAR kapı.
 *
 * Oturum bypass'ı yalnız "üretim modu değil" şartına bakar; bu, dağıtılmış bir
 * staging build'ini de kapsayabilir. Ödeme kapısını açan bir yol için bu yeterince
 * dar değil: yanlışlıkla üretim-dışı bir mod ile derlenmiş bir paket dağıtılırsa
 * tek bir localStorage anahtarı herkesi Pro yapardı.
 *
 * `import.meta.env.DEV` yalnız Vite **geliştirme sunucusunda** true'dur ve HİÇBİR
 * `vite build` çıktısında true olmaz — mod ne olursa olsun. Gece E2E koşumu dev
 * sunucusuyla çalıştığı için test korunur, dağıtılan hiçbir paket etkilenmez.
 */
export function isE2EEntitlementBypassEnabled(): boolean {
  if (!import.meta.env.DEV) {
    return false
  }
  return isE2EAuthBypassEnabled()
}

/**
 * Üretim DIŞI önizleme hook'u: entitlement bypass'ında (yalnız Vite dev sunucusu) abonelik
 * verisi olmadığından ödeme uyarı kartı `sensei:e2e-payment-issue=payment_failed|card_expiring`
 * ile görüntülenebilir. Aynı dar kapıya (`isE2EEntitlementBypassEnabled`) bağlıdır.
 */
export const E2E_PAYMENT_ISSUE_STORAGE_KEY = 'sensei:e2e-payment-issue'

export function e2ePaymentIssue(): PaymentIssue | null {
  if (!isE2EEntitlementBypassEnabled()) return null
  try {
    const kind = window.localStorage.getItem(E2E_PAYMENT_ISSUE_STORAGE_KEY)
    return kind === 'payment_failed' || kind === 'card_expiring' ? { kind, deadline: null } : null
  } catch {
    return null
  }
}

/**
 * Üretim DIŞI önizleme hook'u (#1029): entitlement bypass'ı herkesi Pro yapar; Free ekranlarını
 * (kilit simgeleri, kota sayacı, sınır mesajları) görmek için `sensei:e2e-plan=free`
 * kullanıcıyı Free sayar. Aynı dar kapıya (`isE2EEntitlementBypassEnabled`: yalnız Vite dev
 * sunucusu + e2e-auth açık) bağlıdır; `vite build` çıktısında hiçbir etkisi yoktur.
 */
export const E2E_PLAN_STORAGE_KEY = 'sensei:e2e-plan'

export function isE2EFreePlan(): boolean {
  if (!isE2EEntitlementBypassEnabled()) return false
  try {
    return window.localStorage.getItem(E2E_PLAN_STORAGE_KEY) === 'free'
  } catch {
    return false
  }
}
